import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { listMyProbationReviews } from '@/api/services/probation.service';
import { ProbationStatusBadge } from './ProbationStatusBadge';
import { ProbationCaseDialog } from './ProbationCaseDialog';
import { daysUntil, formatDate, normalizeCase, relativeEnd } from './probationFormat';
import type { ProbationCase } from '@/api/services/probation.service';

/**
 * Dashboard card for a reporting manager: undecided probation cases of their
 * current reports, each opening a review-only case view. Renders nothing when
 * there is nothing to review or the list cannot be loaded, so employees who
 * manage nobody never see an empty card.
 */
export function ProbationReviewsCard(): React.ReactElement | null {
  const [selected, setSelected] = useState<ProbationCase | null>(null);
  const { data } = useQuery({
    queryKey: ['hr-probation-reviewing'],
    queryFn: listMyProbationReviews,
    retry: false,
  });
  const cases = (Array.isArray(data) ? data : [])
    .map(normalizeCase)
    .filter((item): item is ProbationCase => item !== null);

  if (cases.length === 0) return null;

  return (
    <Card
      header={
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Probation reviews
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {cases.length} {cases.length === 1 ? 'report' : 'reports'}
          </span>
        </div>
      }
      contentClassName="p-0"
    >
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {cases.map((item) => {
          const days = daysUntil(item.currentEndDate);
          const relative = relativeEnd(days);
          const overdue = days !== null && days < 0;
          return (
            <li
              key={item._id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {item.employee.name}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Ends {formatDate(item.currentEndDate)}
                  {relative && (
                    <span className={overdue ? 'ml-1 font-medium text-danger' : 'ml-1'}>
                      ({relative})
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ProbationStatusBadge status={item.status} />
                <Button size="sm" variant="secondary" onClick={() => setSelected(item)}>
                  {item.review ? 'View review' : 'Review'}{' '}
                  <span className="sr-only">probation of {item.employee.name}</span>
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {selected && (
        <ProbationCaseDialog
          key={selected._id}
          companyId={selected.companyId}
          caseId={selected._id}
          reviewerOnly
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}
